import { useParams, useNavigate } from 'react-router';
import { ArrowLeft, List } from 'lucide-react';
import { useJobs, useJobCandidates, useMoveCandidate } from '@/api/queries/recruitment.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

const STAGES = ['APPLIED','SCREENED','INTERVIEW','OFFERED','HIRED','REJECTED'];
const STAGE_STYLE = {
  APPLIED:   { bg: 'bg-blue-50 dark:bg-blue-900/20',   border: 'border-blue-200 dark:border-blue-800',   dot: 'bg-blue-500'   },
  SCREENED:  { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', dot: 'bg-purple-500' },
  INTERVIEW: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', dot: 'bg-yellow-500' },
  OFFERED:   { bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', dot: 'bg-orange-500' },
  HIRED:     { bg: 'bg-green-50 dark:bg-green-900/20',   border: 'border-green-200 dark:border-green-800',   dot: 'bg-green-500'  },
  REJECTED:  { bg: 'bg-red-50 dark:bg-red-900/20',     border: 'border-red-200 dark:border-red-800',     dot: 'bg-red-400'    },
};

function CandidateCard({ candidate, onMove }) {
  const nextStages = STAGES.filter(s => s !== candidate.stage && s !== 'REJECTED');
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-[12px] p-3 shadow-sm hover:shadow-md transition-shadow">
      <p className="text-[13px] font-bold text-[var(--text-primary)]">{candidate.name}</p>
      {candidate.experience && <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{candidate.experience}</p>}
      {candidate.notes && <p className="text-[11px] text-[var(--text-secondary)] mt-1 line-clamp-2">{candidate.notes}</p>}
      <div className="mt-2 pt-2 border-t border-[var(--border)] flex flex-wrap gap-1">
        {nextStages.slice(0, 3).map(stage => (
          <button key={stage} onClick={() => onMove(candidate.id, stage)}
            className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[var(--bg-muted)] text-[var(--text-secondary)] hover:bg-[var(--accent)] hover:text-white transition-colors">
            → {stage}
          </button>
        ))}
        {candidate.stage !== 'REJECTED' && (
          <button onClick={() => onMove(candidate.id, 'REJECTED')}
            className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[var(--bg-muted)] text-red-500 hover:bg-red-500 hover:text-white transition-colors">
            Reject
          </button>
        )}
      </div>
    </div>
  );
}

export default function JobPipelinePage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { data: jobsResp } = useJobs();
  const { data: candidatesResp } = useJobCandidates(jobId);
  const moveCandidate = useMoveCandidate();

  const job = jobsResp?.data?.find(j => j.id === jobId);
  const candidates = candidatesResp?.data || [];

  const byStage = STAGES.reduce((acc, s) => {
    acc[s] = candidates.filter(c => c.stage === s);
    return acc;
  }, {});

  const handleMove = (candidateId, stage) => {
    moveCandidate.mutate({ id: candidateId, stage, jobId });
  };

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title={job?.title || 'Pipeline'}
        subtitle="ATS Kanban — click a stage button to move a candidate"
        breadcrumb={[{ label: 'Recruitment', href: '/recruitment' }, { label: job?.title || 'Job' }, { label: 'Pipeline' }]}
        actions={
          <div className="flex gap-2">
            <Button size="sm" icon={List} variant="outline" onClick={() => navigate(`/recruitment/${jobId}`)}>List View</Button>
            <button onClick={() => navigate('/recruitment')} className="flex items-center gap-1.5 text-[13px] text-[var(--text-secondary)]"><ArrowLeft size={14}/> Back</button>
          </div>
        }
      />

      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map(stage => {
          const style = STAGE_STYLE[stage];
          const cols = byStage[stage];
          return (
            <div key={stage} className={cn('rounded-[14px] border p-3 min-w-[200px] flex-shrink-0 space-y-3', style.bg, style.border)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cn('w-2 h-2 rounded-full', style.dot)}/>
                  <p className="text-[12px] font-bold text-[var(--text-primary)]">{stage}</p>
                </div>
                <span className="text-[11px] font-bold text-[var(--text-tertiary)] bg-[var(--bg-card)] px-2 py-0.5 rounded-full">{cols.length}</span>
              </div>
              <div className="space-y-2">
                {cols.map(c => <CandidateCard key={c.id} candidate={c} onMove={handleMove}/>)}
                {cols.length === 0 && (
                  <div className="text-center py-6 text-[11px] text-[var(--text-tertiary)] border-2 border-dashed border-[var(--border)] rounded-[10px]">
                    No candidates
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
